<!-- Container: picks the card variant by product type; cart logic and outputs stay here.
     Variants get the shared parts (media, badges, cart count, mobile buy, footer) as templates and lay them out
     themselves as a flat grid (styles/themable/_product-card.scss). -->
@switch (cardType) {
  @case ('request') {
    <cake-item-request
      [cake]="cake"
      [productTypeBuilderUrl]="buildCakeUrl"
      [blocks]="{ media: mediaTpl, badges: cakeItemBadgesTpl, cartCount: cartCountTpl, buy: buyTpl, footer: footerTpl }"
      [cardClasses]="cardClasses"
      (open)="orderNow()"
      (mediaClick)="orderNow()"
      (focusout)="hideControls()"
    ></cake-item-request>
  }

  @case ('complex') {
    <cake-item-complex
      [cake]="cake"
      [cartCount]="cartCount"
      [productTypeBuilderUrl]="buildCakeUrl"
      [blocks]="{ media: mediaTpl, badges: cakeItemBadgesTpl, cartCount: cartCountTpl, buy: buyTpl, footer: footerTpl }"
      [cardClasses]="cardClasses"
      [controlsVisible]="isControlsShowing"
      [touchLocked]="isTouchDevice && !!cartCount"
      (open)="orderNow()"
      (edit)="editExisting()"
      (remove)="onRemoveOrderLine.emit(cartLine); hideControls();"
      (mediaClick)="onComplexOverlayClick()"
      (focusout)="hideControls()"
    ></cake-item-complex>
  }

  @case ('pieces') {
    <cake-item-pieces
      [cake]="cake"
      [cartCount]="cartCount"
      [productTypeBuilderUrl]="buildCakeUrl"
      [blocks]="{ media: mediaTpl, badges: cakeItemBadgesTpl, cartCount: cartCountTpl, buy: buyTpl, footer: footerTpl }"
      [cardClasses]="cardClasses"
      (open)="orderNowPiecesCake()"
      (buy)="saveOnePiecesCake()"
      (countChange)="count$.next($event)"
      (mediaClick)="onPiecesOverlayClick()"
      (focusout)="hideControls()"
    ></cake-item-pieces>
  }

  @case ('catering') {
    <cake-item-catering
      [cake]="cake"
      [hasCartLines]="hasCateringBuilds"
      [productTypeBuilderUrl]="buildCakeUrl"
      [blocks]="{ media: mediaTpl, badges: cakeItemBadgesTpl, cartCount: cartCountTpl, buy: buyTpl, footer: footerTpl }"
      [cardClasses]="cardClasses"
      [controlsVisible]="isControlsShowing"
      [touchLocked]="isTouchDevice && hasCateringBuilds"
      (open)="orderNow()"
      (edit)="editCateringExisting()"
      (remove)="onRemoveCateringMenu.emit(cake.id); hideControls()"
      (mediaClick)="onCateringOverlayClick()"
      (focusout)="hideControls()"
    ></cake-item-catering>
  }

  @case ('pieces-group') {
    <cake-item-pieces-group
      [cake]="cake"
      [cartCount]="cartCount"
      [productTypeBuilderUrl]="buildCakeUrl"
      [blocks]="{ media: mediaTpl, badges: cakeItemBadgesTpl, cartCount: cartCountTpl, buy: buyTpl, footer: footerTpl }"
      [cardClasses]="cardClasses"
      [controlsVisible]="isControlsShowing"
      [touchLocked]="isTouchDevice && !!cartCount"
      (open)="orderNow()"
      (edit)="editExisting()"
      (remove)="onRemoveOrderLine.emit(cartLine); hideControls();"
      (mediaClick)="onPiecesGroupOverlayClick()"
      (focusout)="hideControls()"
    ></cake-item-pieces-group>
  }

  @default {
    <!-- a structure type without an overlay: the shared parts only -->
    <div class="ws-product-card" [ngClass]="cardClasses">
      <ng-container *ngTemplateOutlet="mediaTpl"></ng-container>
      <ng-container *ngTemplateOutlet="cakeItemBadgesTpl"></ng-container>
      <ng-container *ngTemplateOutlet="cartCountTpl"></ng-container>
      <ng-container *ngTemplateOutlet="buyTpl"></ng-container>
      <ng-container *ngTemplateOutlet="footerTpl"></ng-container>
    </div>
  }
}

<ng-template #mediaTpl>
  @if (cake.image) {
    <div class="ws-product-card__image ws-product-card__media-part"
         [style.backgroundImage]="'url(' + (cake.image | multiUrlImage: 'original' : '/assets/img/content/default-cake-image.jpg') + ')'">
    </div>
  } @else {
    <no-image-cake-item
      [cake]="cake"
      [isPriceVisible]="!isCateringMenu && !isRequestForm"
      [productTypeBuilderUrl]="buildCakeUrl"
      [currency]="bakeryCurrency"
      [pieces]="extendedStructureType.piecesGroup || extendedStructureType.pieces"
    ></no-image-cake-item>
  }
</ng-template>

<ng-template #footerTpl>
  @if (cake.image) {
    <cake-card-footer
      [cake]="cake"
      [isPriceVisible]="!isCateringMenu && !isRequestForm"
      [currency]="bakeryCurrency"
      [productTypeBuilderUrl]="buildCakeUrl"
      [showBakeryLine]="showBakeryLine"
      [bakeryOverviewUrl]="bakeryOverviewUrl"
      (onCakeNameClick)="onCakeNameClick()"
      (onShowPickup)="onShowPickup.emit()"
      (onShowDelivery)="onShowDelivery.emit()"
    ></cake-card-footer>
  }
</ng-template>

<ng-template #cartCountTpl>
  @if (cartCount && !isCateringMenu) {
    <div class="ws-product-card__cart-count ws-product-card__media-part"
         [class.d-none]="isPiecesWithOneVariant && cake.showQuantityPicker"
         [class.d-md-block]="isPiecesWithOneVariant">
      {{cartCount}}
    </div>
  }
</ng-template>

<!-- the media's own mobile "Buy" (not part of the overlay) -->
<ng-template #buyTpl>
  @if (!isPiecesWithOneVariant && !isCateringMenu && !cartCount) {
    <button class="btn ws-button ws-button--primary ws-product-card__buy ws-product-card__only-touch"
            [translate]="'orderProcess.cakesOverview.cakeItem.buy'"
            (click)="orderNow()">Buy
    </button>
  }
</ng-template>

<ng-template #cakeItemBadgesTpl>
  @if (showSameDayBadge || cake.discount?.value || (showCustomNote && noteLabel) || cake.mailDeliveryAvailable) {
    <div class="ws-product-card__badges ws-product-card__media-part">
      @if (showSameDayBadge) {
        <div>
          <ng-container *ngTemplateOutlet="sameDayDeliveryBadge"></ng-container>
        </div>
      }

      @if (cake.discount?.value) {
        <ui-discount-badge class="ws-product-card__discount" [value]="cake.discount?.value"></ui-discount-badge>
      }

      @if (showCustomNote && noteLabel) {
        @if (noteLabel?.useCustomColor && noteLabel?.bgColor) {
          <div class="badge ws-product-card__badge"
               [style.background-color]="noteLabel.bgColor"
               [style.color]="noteLabel.textColor">
            {{ noteLabel.text }}
          </div>
        } @else {
          <div class="badge ws-product-card__badge {{ CUSTOM_NOTE_COLOR_MAP[noteLabel?.colorType] }}"
               [style.color]="noteLabel?.textColor">
            {{ noteLabel.text }}
          </div>
        }
      } @else if (cake.mailDeliveryAvailable) {
        <div>
          @if (!isControlsShowing) {
            <div class="badge badge-primary ws-product-card__badge ws-product-card__badge--mail"
                 [translate]="'orderProcess.cakesOverview.cakeItem.canBeSentByMail'">Can be sent by mail
            </div>
          }
        </div>
      }
    </div>
  }
</ng-template>

<ng-template #sameDayDeliveryBadge>
  <div class="badge ws-product-card__badge"
       [style.background-color]="cake?.sameDayDeliverySettings?.badgeColor"
       [style.color]="cake?.sameDayDeliverySettings?.badgeTextColor">
    {{ cake?.sameDayDeliverySettings?.badgeText }}
  </div>
</ng-template>
